import { Suspense, lazy, useEffect, useMemo } from 'react';
import { useSelector } from '@/store/hooks';
import { Modals } from './enums/Modals';
import { selectServiceModalSlice } from './store/selectors';

const LazyAdminUserEditModal = lazy(() => import('./components/AdminUserEditModal/AdminUserEditModal'));
const LazyClaimSetCreationModal = lazy(() => import('./components/ClaimSetCreationModal/ClaimSetCreationModal'));
const LazyConfirmationModal = lazy(() => import('./components/ConfirmationModal/ConfirmationModal'));
const LazyPotentialPolicyModal = lazy(() => import('./components/PotentialPolicyModal/PotentialPolicyModal'));
const LazyReportClaimModal = lazy(() => import('./components/ReportClaimModal/ReportClaimModal'));
const LazyStatusReasonModal = lazy(() => import('./components/StatusReasonModal/StatusReasonModal'));
const LazyAddContactModal = lazy(() => import('./components/AddContactModal/AddContactModal'));
const LazyLinkPolicyModal = lazy(() => import('./components/LinkPolicyModal/LinkPolicyModal'));
const LazyAddContactInfoModal = lazy(() => import('./components/AddContactInfoModal/AddContactInfoModal'));
const LazyAddAddressModal = lazy(() => import('./components/AddAddressModal/AddAddressModal'));
const UpdateAddressModal = lazy(() => import('./components/UpdateAddressModal/UpdateAddressModal'));
const AddPotentialBeneficiaryModal = lazy(() => import('./components/AddPotentialBeneficiary/AddPotentialBeneficiary'));
const AddClaimant = lazy(() => import('./components/AddClaimant/AddClaimant'));
const LazyDocumentPreviewModal = lazy(() => import('./components/DocumentPreviewModal/DocumentPreviewModal'));
const LazyAddTaskModal = lazy(() => import('./components/AddTaskModal/AddTaskModal'));
const LazyAddCoverageModal = lazy(() => import('./components/AddCoverageModal/AddCoverageModal'));
const LazyLinkCoverageModal = lazy(() => import('./components/LinkCoverageModal/LinkCoverageModal'));
const LazyAddClaimModal = lazy(() => import('./components/AddClaimModal/AddClaimModal'));
const LazyNoteModal = lazy(() => import('./components/NoteModal/NoteModal'));
const LazyMilestoneModal = lazy(
  () => import('@/domains/milestones/components/MilestoneEntityModal/MilestoneEntityModal'),
);
const LazyAddRequestedItemModal = lazy(
  () => import('@/domains/milestones/components/AddRequestedItemModal/AddRequestedItemModal'),
);
const LazyUpdateRequestedItemModal = lazy(
  () => import('@/domains/milestones/components/UpdateRequestedItemModal/UpdateRequestedItemModal'),
);
const LazyDeleteRequestedItemModal = lazy(
  () => import('@/domains/milestones/components/DeleteRequestedItemModal/DeleteRequestedItemModal'),
);
const LazyAddInsuredModal = lazy(() => import('./components/AddInsuredModal/AddInsuredModal'));
const LazyAddCustomInboxModal = lazy(() => import('./components/AddCustomInboxModal/AddCustomInboxModal'));
const LazyAddPaymentModal = lazy(
  () => import('@/domains/payments/features/paymentModals/modals/AddPayment/AddPaymentModal'),
);
const LazyEditPaymentMethodModal = lazy(
  () => import('@/domains/payments/features/paymentModals/modals/EditPaymentMethod/EditPaymentMethodModal'),
);
const LazyAddPaymentMethodModal = lazy(
  () => import('@/domains/payments/features/paymentModals/modals/AddPaymentMethod/AddPaymentMethodModal'),
);
const LazyAddPaymentAdjustmentModal = lazy(
  () =>
    import(
      '@/domains/payments/features/paymentModals/modals/adjustments/AddPaymentAdjustment/AddPaymentAdjustmentModal'
    ),
);
const LazyAddBenefitAdjustmentModal = lazy(
  () =>
    import(
      '@/domains/payments/features/paymentModals/modals/adjustments/AddBenefitAdjustment/AddBenefitAdjustmentModal'
    ),
);
const LazyAddPayeeToPaymentModal = lazy(
  () => import('@/domains/payments/features/paymentModals/modals/payee/AddPayeeToPayment/AddPayeeToPaymentModal'),
);
const LazyAddPayeeToPaymentAdjustmentModal = lazy(
  () =>
    import(
      '@/domains/payments/features/paymentModals/modals/payee/AddPayeeToPaymentAdjustment/AddPayeeToPaymentAdjustmentModal'
    ),
);
const LazyAddPayeeToBenefitAdjustmentModal = lazy(
  () =>
    import(
      '@/domains/payments/features/paymentModals/modals/payee/AddPayeeToBenefitAdjustment/AddPayeeToBenefitAdjustmentModal'
    ),
);
const LazyAddPaymentSplitModal = lazy(
  () => import('@/domains/payments/features/paymentModals/modals/AddPaymentSplit/AddPaymentSplitModal'),
);
const LazyEditBenefitCalculationModal = lazy(
  () => import('./components/EditBenefitCalculationModal/EditBenefitCalculationModal'),
);
const LazyOverrideBenefitCalculationModal = lazy(
  () => import('./components/OverrideBenefitCalculationModal/OverrideBenefitCalculationModal'),
);
const LazyDocumentGenerationModal = lazy(() => import('./components/DocumentGenerationModal/DocumentGenerationModal'));
const LazyDocumentVersionHistoryModal = lazy(
  () => import('@/features/serviceModal/components/DocumentVersionHistoryModal/DocumentVersionHistoryModal'),
);
const LazyDocumentStatusHistoryModal = lazy(
  () =>
    import(
      '@/features/serviceModal/components/BaseStatusHistoryModal/modals/DocumentStatusHistoryModal/DocumentStatusHistoryModal'
    ),
);
const LazyDocumentParametersAndVariablesModal = lazy(
  () =>
    import(
      '@/features/serviceModal/components/DocumentParametersAndVariablesModal/DocumentParametersAndVariablesModal'
    ),
);
const LazyCorrespondenceStatusHistoryModal = lazy(
  () =>
    import(
      '@/features/serviceModal/components/BaseStatusHistoryModal/modals/CorrespondenceStatusHistoryModal/CorrespondenceStatusHistoryModal'
    ),
);
const LazyPdfViewerModal = lazy(() => import('@/features/serviceModal/components/PdfViewerModal/PdfViewerModal'));
const LazyCorrespondenceModal = lazy(
  () => import('@/features/serviceModal/components/CorrespondenceModal/CorrespondenceModal'),
);
const LazyEditAssignmentQueueAccountModal = lazy(
  () => import('@/features/serviceModal/components/EditAssignmentQueueAccountModal/EditAssignmentQueueAccountModal'),
);
const LazyAddAssignmentQueueAccountModal = lazy(
  () => import('@/features/serviceModal/components/AddAssignmentQueueAccountModal/AddAssignmentQueueAccountModal'),
);
const LazyDataTableModal = lazy(() => import('@/features/dataTable/components/DataTableModal/DataTableModal'));

const LazyLinkProducerModal = lazy(
  () => import('@/features/serviceModal/components/LinkProducerModal/LinkProducerModal'),
);

const LazyAddProducerModal = lazy(() => import('@/features/serviceModal/components/AddProducerModal/AddProducerModal'));

let index = 2000;

const ServiceModal = () => {
  const modals = useSelector(selectServiceModalSlice);

  const modalKeys = useMemo(() => {
    return Object.keys(modals);
  }, [modals]);

  useEffect(() => {
    modalKeys.length > 0 ? (document.body.style.overflow = 'hidden') : (document.body.style.overflow = '');

    return () => {
      document.body.style.overflow = '';
    };
  }, [modalKeys]);

  const renderModal = (modalKey: Modals) => {
    switch (modalKey) {
      case Modals.AdminUserEdit:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAdminUserEditModal zIndex={index++} />
          </Suspense>
        );
      case Modals.ClaimSetCreation:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyClaimSetCreationModal zIndex={index++} />
          </Suspense>
        );
      case Modals.Confirmation:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyConfirmationModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddPotentialPolicy:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyPotentialPolicyModal zIndex={index++} />
          </Suspense>
        );
      case Modals.StatusReason:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyStatusReasonModal zIndex={index++} />
          </Suspense>
        );
      case Modals.ReportClaim:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyReportClaimModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddContact:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddContactModal zIndex={index++} />
          </Suspense>
        );
      case Modals.LinkPolicy:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyLinkPolicyModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddContactInfo:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddContactInfoModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddAddress:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddAddressModal zIndex={index++} />
          </Suspense>
        );
      case Modals.UpdateAddress:
        return (
          <Suspense key={modalKey} fallback={null}>
            <UpdateAddressModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddPotentialBeneficiary:
        return (
          <Suspense key={modalKey} fallback={null}>
            <AddPotentialBeneficiaryModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddClaimant:
        return (
          <Suspense key={modalKey} fallback={null}>
            <AddClaimant zIndex={index++} />
          </Suspense>
        );
      case Modals.AddCoverage:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddCoverageModal zIndex={index++} />
          </Suspense>
        );
      case Modals.LinkCoverage:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyLinkCoverageModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddClaim:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddClaimModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddTask:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddTaskModal zIndex={index++} />
          </Suspense>
        );
      case Modals.Milestone:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyMilestoneModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddRequestedItem:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddRequestedItemModal zIndex={index++} />
          </Suspense>
        );
      case Modals.UpdateRequestedItem:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyUpdateRequestedItemModal zIndex={index++} />
          </Suspense>
        );
      case Modals.DeleteRequestedItem:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyDeleteRequestedItemModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddInsured:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddInsuredModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddCustomInbox:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddCustomInboxModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddPayment:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddPaymentModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddPaymentSplit:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddPaymentSplitModal zIndex={index++} />
          </Suspense>
        );
      case Modals.EditPaymentMethod:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyEditPaymentMethodModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddPaymentMethod:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddPaymentMethodModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddPaymentAdjustment:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddPaymentAdjustmentModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddBenefitAdjustment:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddBenefitAdjustmentModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddPayeeToPayment:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddPayeeToPaymentModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddPayeeToPaymentAdjustment:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddPayeeToPaymentAdjustmentModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddPayeeToBenefitAdjustment:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddPayeeToBenefitAdjustmentModal zIndex={index++} />
          </Suspense>
        );
      case Modals.OverrideBenefitCalculation:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyOverrideBenefitCalculationModal zIndex={index++} />
          </Suspense>
        );
      case Modals.EditBenefitCalculation:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyEditBenefitCalculationModal zIndex={index++} />
          </Suspense>
        );
      case Modals.DocumentGeneration:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyDocumentGenerationModal zIndex={index++} />
          </Suspense>
        );
      case Modals.DocumentVersionHistory:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyDocumentVersionHistoryModal zIndex={index++} />
          </Suspense>
        );
      case Modals.DocumentStatusHistory:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyDocumentStatusHistoryModal zIndex={index++} />
          </Suspense>
        );
      case Modals.DocumentParametersAndVariables:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyDocumentParametersAndVariablesModal zIndex={index++} />
          </Suspense>
        );
      case Modals.DocumentPreviewModal:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyDocumentPreviewModal zIndex={index++} />
          </Suspense>
        );
      case Modals.PdfViewer:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyPdfViewerModal zIndex={index++} />
          </Suspense>
        );
      case Modals.CorrespondenceStatusHistory:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyCorrespondenceStatusHistoryModal zIndex={index++} />
          </Suspense>
        );
      case Modals.Correspondence:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyCorrespondenceModal zIndex={index++} />
          </Suspense>
        );
      case Modals.Note:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyNoteModal zIndex={index++} />
          </Suspense>
        );
      case Modals.EditAssignmentQueueAccount:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyEditAssignmentQueueAccountModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddAssignmentQueueAccount:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddAssignmentQueueAccountModal zIndex={index++} />
          </Suspense>
        );
      case Modals.LinkProducer:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyLinkProducerModal zIndex={index++} />
          </Suspense>
        );
      case Modals.DataTable:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyDataTableModal zIndex={index++} />
          </Suspense>
        );
      case Modals.AddProducer:
        return (
          <Suspense key={modalKey} fallback={null}>
            <LazyAddProducerModal zIndex={index++} />
          </Suspense>
        );
      default:
        return null;
    }
  };

  return <>{modalKeys.map((key) => renderModal(key as Modals))}</>;
};

export default ServiceModal;
